import type {
  ConsumableTypeKey,
  ConsumableUnitKey,
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import { ConsumableCadence } from "@/services/backend/qpm/types/enums";
import type { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";

export interface MonitorSnapDTO {
  sourceId: string;
  name: string;
  supplier: string;
  type: MonitorType;

  times: ServiceTimesDTO;
  capabilities: CapabilitiesDTO;

  basePrice?: PriceAtDTO;
  recommendedVisits: number;

  image?: AssetRefDTO;
  details: DetailsDTO;

  pests: PestSnapDTO[];
  additionals: MonitorAdditionalDTO[];
  consumables: ConsumableLinkDTO[];

  rentingDefaults: Record<number, number>;
  takenAt: string;
}

export interface ServiceTimesDTO {
  install: number;
  maintain: number;
}

export interface CapabilitiesDTO {
  permanentBaiting: boolean;
  hasLethalAgent: boolean;
  rentable: boolean;
  rentalOnly: boolean;
  checkpointVisual: boolean;
  includeInCheckpointService: boolean;
  iot?: IoTSpecDTO;
  flyEx?: FlyExSpecDTO;
}

export interface IoTSpecDTO {
  protocol: string;
  class: MonitorIoTClass;
}

export type FlyExSpecDTO = Record<string, never>;

export interface PriceAtDTO {
  price: number;
  effectiveOn: string;
}

export interface AssetRefDTO {
  fileId?: string;
  fileUuid?: string;
  prefix: string;
  addedAt: string;
}

export interface DetailsDTO {
  description: string;
  installationType: string;
  heightMM: number;
  widthMM: number;
  depthMM: number;
  weightG: number;
  coverageValue: number;
  coveragePreText: string;
  coveragePostText: string;
  starterSpecification: string;
  protectionLevel: string;
}

export interface PestSnapDTO {
  sourceId: string;
  key: string;
  kind: PestKind;
  name: string;
  abbreviation: string;
  description: string;
  synonyms: string[];
  createdAt: string;
  updatedAt: string;
  takenAt: string;
}

export interface ConsumableSnapDTO {
  id: string;
  key: string;
  name: string;
  type: ConsumableTypeKey;
  unit: ConsumableUnitKey;
  spec: unknown;
  compatibilityTag: string;
  sku: string;
  basePrice?: {
    price: number;
    effectiveOn: string;
  };
  takenAt: string;
}

export interface ConsumableLinkDTO {
  consumable: ConsumableSnapDTO;

  quantityPerDevice: number;
  cadence: ConsumableCadence;
  customAmountOfMeasures?: number;

  // Legacy only
  perDevice?: number;
  perYear?: number;

  note: string;
  linkedAt: string;
  overridePrice?: PriceAtDTO;
}

export interface MonitorAdditionalDTO {
  id: string;
  name: string;
  description: string;
  basePrice: number;
  rentingFees: Record<number, number>;
  config?: MonitorAdditionalConfigDTO;
  image?: AssetRefDTO;
  createdAt?: string;
  updatedAt?: string;
}

export type MonitorAdditionalSelectionMode =
  "ALWAYS_REQUIRED" | "OPTIONAL" | "DEFAULT_SELECTED_OPTIONAL";

export interface MonitorAdditionalConfigDTO {
  includeInMaterialCalculation: boolean;
  includeInRentingCalculation: boolean;
  includeInInstallationCalculation: boolean;
  includeInServiceCalculation: boolean;
  selectionMode: MonitorAdditionalSelectionMode;
  showInOfferMonitorCatalog: boolean;
}

export const defaultMonitorAdditionalConfig: MonitorAdditionalConfigDTO = {
  includeInMaterialCalculation: true,
  includeInRentingCalculation: false,
  includeInInstallationCalculation: false,
  includeInServiceCalculation: false,
  selectionMode: "OPTIONAL",
  showInOfferMonitorCatalog: true,
};

export function normalizeMonitorAdditionalConfig(
  config?: Partial<MonitorAdditionalConfigDTO>,
): MonitorAdditionalConfigDTO {
  return {
    ...defaultMonitorAdditionalConfig,
    ...config,
    selectionMode: config?.selectionMode ?? "OPTIONAL",
  };
}

export interface MonitorAdditionalLibraryItemDTO extends MonitorAdditionalDTO {
  monitorType: MonitorType;
  sourceMonitorId: string;
  sourceMonitorName: string;
}
